// ============================================================
// BankDetails.jsx - COMPLETE RESPONSIVE VERSION
// ============================================================

import React, { useState, useEffect } from "react";
import {
    Card, Form, Input, Button, message, Spin, Alert, Divider,
    Tag, Space, Typography,
} from "antd";
import {
    BankOutlined, IdcardOutlined, QrcodeOutlined,
    CheckCircleOutlined, EditOutlined, SaveOutlined,
    UserOutlined, CalendarOutlined,
} from "@ant-design/icons";
import { updateBankDetails, getUserBankDetails } from "../../Api/CoreApi";

const { Title, Text } = Typography;

function BankDetails() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [bankDetails, setBankDetails] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const fetchBankDetails = async () => {
        try {
            setLoading(true);
            const data = await getUserBankDetails();
            setBankDetails(data);
            form.setFieldsValue({
                bank_account: data.bank_account || "",
                ifsc_code: data.ifsc_code || "",
                upi_id: data.upi_id || "",
            });
            if (!data.bank_account && !data.ifsc_code && !data.upi_id) {
                setIsEditing(true);
            }
        } catch (error) {
            message.error("Failed to fetch bank details");
            setBankDetails({ bank_account: '', ifsc_code: '', upi_id: '', bank_updated_at: null });
            setIsEditing(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBankDetails();
    }, []);

    const handleSubmit = async (values) => {
        try {
            setSaving(true);
            await updateBankDetails(values);
            message.success("✅ Bank details updated successfully!");
            setIsEditing(false);
            await fetchBankDetails();
        } catch (error) {
            const errorMsg = error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "Failed to update bank details";
            message.error(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const maskAccount = (acc) => {
        if (!acc || acc.length < 4) return acc || "Not set";
        return "****" + acc.slice(-4);
    };

    const formatDate = (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric"
        });
    };

    // ============================================================
    // STYLES
    // ============================================================
    const styles = {
        page: {
            padding: '24px 16px',
            maxWidth: '900px',
            margin: '0 auto',
            fontFamily: 'Poppins, sans-serif',
            background: '#fff',
            minHeight: '100vh',
        },
        header: {
            marginBottom: '28px',
            padding: '20px 22px',
            background: 'linear-gradient(135deg, #fff0f3, #ffe4ec)',
            border: '2px solid #ffe4ec',
            borderRadius: '18px',
        },
        titleWrapper: {
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
        },
        headerIcon: {
            fontSize: '26px',
            color: '#fff',
            padding: '14px',
            background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
            borderRadius: '14px',
            boxShadow: '0 6px 18px rgba(255,0,85,.22)',
        },
        title: {
            margin: 0,
            fontSize: '24px',
            fontWeight: 800,
            color: '#1a0a10',
        },
        loading: {
            textAlign: 'center',
            padding: '60px 20px',
        },
        loadingText: {
            marginTop: '16px',
            color: '#888',
            fontSize: '14px',
        },
        editCard: {
            border: '2px solid #ff0055',
            borderRadius: '20px',
            boxShadow: '0 8px 28px rgba(255,0,85,.1)',
            marginBottom: '22px',
            overflow: 'hidden',
        },
        editHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '17px',
            fontWeight: 800,
            color: '#c2185b',
            marginBottom: '22px',
            paddingBottom: '14px',
            borderBottom: '2px solid #fff0f3',
        },
        editIcon: {
            fontSize: '18px',
            color: '#fff',
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(255,0,85,.18)',
            flexShrink: 0,
        },
        formGrid: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '18px',
        },
        formActions: {
            display: 'flex',
            gap: '12px',
            marginTop: '22px',
            paddingTop: '22px',
            borderTop: '2px solid #fff0f3',
            flexWrap: 'wrap',
        },
        saveBtn: {
            minWidth: '200px',
            height: '48px',
            borderRadius: '999px',
            background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
            border: 'none',
            fontWeight: 700,
            fontSize: '15px',
            boxShadow: '0 8px 22px rgba(255,0,85,.22)',
        },
        viewCard: {
            border: '2px solid #ffe4ec',
            borderRadius: '20px',
            boxShadow: '0 6px 22px rgba(255,0,85,.05)',
            marginBottom: '22px',
            overflow: 'hidden',
        },
        viewHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        infoGrid: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '18px',
        },
        infoItem: {
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
        },
        infoLabel: {
            fontSize: '12px',
            color: '#c2185b',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
        },
        infoValue: {
            fontSize: '15px',
            fontWeight: 600,
            padding: '12px 14px',
            background: 'linear-gradient(135deg, #fffafc, #fff0f3)',
            border: '1.5px solid #ffe4ec',
            borderRadius: '12px',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            color: '#1a0a10',
        },
        accountMasked: {
            marginRight: '8px',
            color: '#555',
        },
        accountFull: {
            fontSize: '14px',
            color: '#ff0055',
            fontWeight: 700,
        },
        alert: {
            marginTop: '16px',
            borderRadius: '12px',
        },
        securityCard: {
            background: 'linear-gradient(135deg, #fffafc, #fff0f3)',
            border: '2px solid #ffe4ec',
            borderRadius: '18px',
            marginBottom: '22px',
            boxShadow: '0 4px 14px rgba(255,0,85,.04)',
        },
        securityContent: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
        },
        securityIcon: {
            fontSize: '24px',
            width: '48px',
            height: '48px',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 6px 16px rgba(255,0,85,.18)',
            flexShrink: 0,
        },
        securityText: {
            display: 'flex',
            flexDirection: 'column',
        },
        securityTextStrong: {
            fontSize: '14px',
            color: '#1a0a10',
            fontWeight: 700,
        },
        securityTextSpan: {
            fontSize: '13px',
            color: '#888',
            marginTop: '2px',
        },
        divider: {
            margin: '14px 0',
            borderColor: '#fff0f3',
        },
    };

    // ============================================================
    // RENDER
    // ============================================================
    return (
        <>
            {/* ✅ RESPONSIVE CSS */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

                /* ── Tablet ≤ 768px ──────────────────────── */
                @media (max-width: 768px) {
                    .bd-page {
                        padding: 16px 12px !important;
                    }
                    .bd-header {
                        padding: 16px 18px !important;
                        margin-bottom: 20px !important;
                        border-radius: 16px !important;
                    }
                    .bd-form-grid {
                        grid-template-columns: 1fr !important;
                        gap: 0 !important;
                    }
                    .bd-info-grid {
                        grid-template-columns: 1fr !important;
                        gap: 14px !important;
                    }
                    .bd-form-actions {
                        flex-direction: column !important;
                        gap: 10px !important;
                    }
                    .bd-save-btn {
                        width: 100% !important;
                        min-width: unset !important;
                    }
                    .bd-cancel-btn {
                        width: 100% !important;
                        justify-content: center !important;
                        text-align: center !important;
                    }
                    .bd-view-header {
                        flex-wrap: wrap !important;
                        gap: 12px !important;
                    }
                    .bd-edit-btn {
                        width: 100% !important;
                    }
                    .bd-security-content {
                        gap: 12px !important;
                    }
                }

                /* ── Mobile ≤ 480px ──────────────────────── */
                @media (max-width: 480px) {
                    .bd-page {
                        padding: 12px 10px !important;
                    }
                    .bd-header {
                        padding: 14px !important;
                        margin-bottom: 16px !important;
                        border-radius: 14px !important;
                    }
                    .bd-title-wrap {
                        gap: 10px !important;
                    }
                    .bd-header-icon {
                        font-size: 20px !important;
                        padding: 10px !important;
                        border-radius: 12px !important;
                    }
                    .bd-title {
                        font-size: 18px !important;
                        margin-bottom: 2px !important;
                    }
                    .bd-subtitle {
                        font-size: 12px !important;
                    }
                    .bd-edit-card,
                    .bd-view-card {
                        border-radius: 16px !important;
                    }
                    .bd-edit-card .ant-card-body,
                    .bd-view-card .ant-card-body,
                    .bd-security-card .ant-card-body {
                        padding: 16px !important;
                    }
                    .bd-edit-header {
                        font-size: 15px !important;
                        margin-bottom: 16px !important;
                        padding-bottom: 12px !important;
                    }
                    .bd-edit-icon {
                        width: 32px !important;
                        height: 32px !important;
                        font-size: 15px !important;
                        border-radius: 9px !important;
                    }
                    .bd-info-value {
                        font-size: 13px !important;
                        padding: 10px 12px !important;
                        border-radius: 10px !important;
                    }
                    .bd-info-label {
                        font-size: 11px !important;
                    }
                    .bd-save-btn {
                        height: 44px !important;
                        font-size: 14px !important;
                        border-radius: 14px !important;
                    }
                    .bd-form-actions {
                        margin-top: 16px !important;
                        padding-top: 16px !important;
                    }
                    .bd-security-card {
                        border-radius: 14px !important;
                        margin-bottom: 16px !important;
                    }
                    .bd-security-icon {
                        width: 42px !important;
                        height: 42px !important;
                        font-size: 20px !important;
                        border-radius: 12px !important;
                    }
                    .bd-security-strong {
                        font-size: 13px !important;
                    }
                    .bd-security-span {
                        font-size: 11.5px !important;
                    }
                    .bd-account-full {
                        display: block !important;
                        margin-top: 4px !important;
                        font-size: 12px !important;
                        margin-right: 0 !important;
                    }
                    .bd-account-masked {
                        font-size: 14px !important;
                    }
                    .bd-divider {
                        margin: 12px 0 !important;
                    }

                    /* Ant Design form overrides */
                    .bd-form-grid .ant-form-item-label > label {
                        font-size: 12.5px !important;
                        font-weight: 600 !important;
                    }
                    .bd-form-grid .ant-input-affix-wrapper {
                        border-radius: 10px !important;
                    }
                    .bd-form-grid .ant-input-lg {
                        font-size: 13px !important;
                    }
                }

                /* ── Extra Small ≤ 360px ─────────────────── */
                @media (max-width: 360px) {
                    .bd-page {
                        padding: 10px 8px !important;
                    }
                    .bd-header {
                        padding: 12px !important;
                    }
                    .bd-title {
                        font-size: 16px !important;
                    }
                    .bd-subtitle {
                        font-size: 11px !important;
                    }
                    .bd-edit-card .ant-card-body,
                    .bd-view-card .ant-card-body,
                    .bd-security-card .ant-card-body {
                        padding: 12px !important;
                    }
                    .bd-info-value {
                        font-size: 12px !important;
                        padding: 9px 10px !important;
                    }
                    .bd-save-btn {
                        height: 42px !important;
                        font-size: 13px !important;
                    }
                    .bd-security-content {
                        flex-direction: column !important;
                        text-align: center !important;
                        align-items: center !important;
                    }
                    .bd-security-text {
                        align-items: center !important;
                        text-align: center !important;
                    }
                }

                /* ── Landscape short ─────────────────────── */
                @media (max-height: 500px) and (orientation: landscape) {
                    .bd-page {
                        padding: 10px 14px !important;
                    }
                    .bd-header {
                        margin-bottom: 12px !important;
                        padding: 12px 16px !important;
                    }
                }
            `}</style>

            <div style={styles.page} className="bd-page">

                {/* ── HEADER ── */}
                <div style={styles.header} className="bd-header">
                    <div style={styles.titleWrapper} className="bd-title-wrap">
                        <BankOutlined style={styles.headerIcon} className="bd-header-icon" />
                        <div>
                            <Title level={2} style={styles.title} className="bd-title">
                                Bank Details
                            </Title>
                            <Text type="secondary" className="bd-subtitle">
                                Manage your bank account information for withdrawals
                            </Text>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div style={styles.loading}>
                        <Spin size="large" />
                        <p style={styles.loadingText}>Loading your bank details...</p>
                    </div>
                ) : (
                    <div>

                        {isEditing ? (
                            /* ── EDIT MODE ── */
                            <Card style={styles.editCard} className="bd-edit-card">
                                <div style={styles.editHeader} className="bd-edit-header">
                                    <div style={styles.editIcon} className="bd-edit-icon">
                                        <EditOutlined />
                                    </div>
                                    <span>Edit Bank Details</span>
                                </div>

                                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                                    <div style={styles.formGrid} className="bd-form-grid">
                                        <Form.Item
                                            name="bank_account"
                                            label="Bank Account Number"
                                            rules={[
                                                { required: true, message: "Please enter bank account number" },
                                                { pattern: /^[0-9]{9,18}$/, message: "Enter valid account number (9-18 digits)" }
                                            ]}
                                        >
                                            <Input
                                                prefix={<BankOutlined />}
                                                placeholder="Enter bank account number"
                                                size="large"
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            name="ifsc_code"
                                            label="IFSC Code"
                                            rules={[
                                                { required: true, message: "Please enter IFSC code" },
                                                { pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, message: "Enter valid IFSC code (e.g., SBIN0001234)" }
                                            ]}
                                        >
                                            <Input
                                                prefix={<IdcardOutlined />}
                                                placeholder="Enter IFSC code"
                                                size="large"
                                                style={{ textTransform: "uppercase" }}
                                                onChange={(e) => {
                                                    e.target.value = e.target.value.toUpperCase();
                                                }}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            name="upi_id"
                                            label="UPI ID (Optional)"
                                            rules={[
                                                { pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/, message: "Enter valid UPI ID (e.g., name@upi)" }
                                            ]}
                                        >
                                            <Input
                                                prefix={<QrcodeOutlined />}
                                                placeholder="Enter UPI ID (e.g., name@upi)"
                                                size="large"
                                            />
                                        </Form.Item>
                                    </div>

                                    <div style={styles.formActions} className="bd-form-actions">
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={saving}
                                            icon={<SaveOutlined />}
                                            size="large"
                                            style={styles.saveBtn}
                                            className="bd-save-btn"
                                        >
                                            Save Bank Details
                                        </Button>
                                        <Button
                                            className="bd-cancel-btn"
                                            onClick={() => {
                                                setIsEditing(false);
                                                form.setFieldsValue({
                                                    bank_account: bankDetails?.bank_account || "",
                                                    ifsc_code: bankDetails?.ifsc_code || "",
                                                    upi_id: bankDetails?.upi_id || "",
                                                });
                                            }}
                                            style={{
                                                borderRadius: '12px',
                                                border: '1.5px solid #ff0055',
                                                color: '#ff0055',
                                                fontWeight: 600,
                                                height: 48,
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </Form>
                            </Card>
                        ) : (
                            /* ── VIEW MODE ── */
                            <Card style={styles.viewCard} className="bd-view-card">
                                <div style={styles.viewHeader} className="bd-view-header">
                                    <div>
                                        {bankDetails?.bank_account ? (
                                            <Tag color="success" icon={<CheckCircleOutlined />}>
                                                Verified
                                            </Tag>
                                        ) : (
                                            <Tag color="warning">Not Set</Tag>
                                        )}
                                    </div>
                                    <Button
                                        type="primary"
                                        icon={<EditOutlined />}
                                        onClick={() => setIsEditing(true)}
                                        className="bd-edit-btn"
                                        style={{
                                            borderRadius: '12px',
                                            background: 'linear-gradient(135deg, #ff0055, #ff4d88)',
                                            border: 'none',
                                            fontWeight: 700,
                                            boxShadow: '0 4px 14px rgba(255,0,85,.2)',
                                        }}
                                    >
                                        Edit Details
                                    </Button>
                                </div>

                                <Divider style={styles.divider} className="bd-divider" />

                                <div style={styles.infoGrid} className="bd-info-grid">
                                    <div style={styles.infoItem}>
                                        <div style={styles.infoLabel} className="bd-info-label">
                                            <BankOutlined /> Bank Account
                                        </div>
                                        <div style={styles.infoValue} className="bd-info-value">
                                            {bankDetails?.bank_account ? (
                                                <>
                                                    <span style={styles.accountMasked} className="bd-account-masked">
                                                        {maskAccount(bankDetails.bank_account)}
                                                    </span>
                                                    <span style={styles.accountFull} className="bd-account-full">
                                                        {bankDetails.bank_account}
                                                    </span>
                                                </>
                                            ) : (
                                                <Text type="secondary">Not set</Text>
                                            )}
                                        </div>
                                    </div>

                                    <div style={styles.infoItem}>
                                        <div style={styles.infoLabel} className="bd-info-label">
                                            <IdcardOutlined /> IFSC Code
                                        </div>
                                        <div style={styles.infoValue} className="bd-info-value">
                                            {bankDetails?.ifsc_code || <Text type="secondary">Not set</Text>}
                                        </div>
                                    </div>

                                    <div style={styles.infoItem}>
                                        <div style={styles.infoLabel} className="bd-info-label">
                                            <QrcodeOutlined /> UPI ID
                                        </div>
                                        <div style={styles.infoValue} className="bd-info-value">
                                            {bankDetails?.upi_id || <Text type="secondary">Not set</Text>}
                                        </div>
                                    </div>

                                    {bankDetails?.bank_updated_at && (
                                        <div style={styles.infoItem}>
                                            <div style={styles.infoLabel} className="bd-info-label">
                                                <CalendarOutlined /> Last Updated
                                            </div>
                                            <div style={styles.infoValue} className="bd-info-value">
                                                {formatDate(bankDetails.bank_updated_at)}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {!bankDetails?.bank_account && (
                                    <Alert
                                        message="No Bank Details Found"
                                        description="Please add your bank account details to enable withdrawals."
                                        type="info"
                                        showIcon
                                        style={styles.alert}
                                    />
                                )}
                            </Card>
                        )}

                        {/* ── SECURITY NOTE ── */}
                        <Card style={styles.securityCard} className="bd-security-card">
                            <div style={styles.securityContent} className="bd-security-content">
                                <div style={styles.securityIcon} className="bd-security-icon">
                                    🔒
                                </div>
                                <div style={styles.securityText} className="bd-security-text">
                                    <strong style={styles.securityTextStrong} className="bd-security-strong">
                                        Your information is secure
                                    </strong>
                                    <span style={styles.securityTextSpan} className="bd-security-span">
                                        Your bank details are encrypted and only used for processing withdrawals.
                                    </span>
                                </div>
                            </div>
                        </Card>
                    </div>
                )}
            </div>
        </>
    );
}

export default BankDetails;